import type { MemoryProposalDto } from '@fos/shared/contracts/context';
import {
  MEMORY_PROPOSAL_STATUS_LABELS,
  MEMORY_PROPOSAL_STATUS_TONES,
  MEMORY_SOURCE_TYPE_LABELS,
} from '@fos/shared/labels';
import { Check, ShieldCheck, Sparkles, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatDateTimeShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { ContextBanner } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { Status } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';
import { DiffView } from '../../knowledge/components/DiffView';
import { TOUCH_BUTTON } from '../../mobile';

type Filter = 'proposed' | 'all';

function ProposalItem({ proposal }: { proposal: MemoryProposalDto }) {
  const utils = trpc.useUtils();
  const refresh = () => {
    void utils.context.listProposals.invalidate();
    void utils.context.overview.invalidate();
    void utils.context.listVersions.invalidate({ kind: proposal.profileKind });
  };
  const onError = (err: unknown) =>
    toast.error(appErrorCode(err) === 'CONFLICT' ? 'Propozycja jest nieaktualna' : 'Nie udało się zapisać decyzji', {
      description: errorMessage(err),
    });
  const accept = trpc.context.acceptProposal.useMutation({
    onSuccess: (p) => {
      toast.success(`${p.file}: zapisano wersję ${p.resultingVersion ?? ''}`.trim());
      refresh();
    },
    onError,
  });
  const reject = trpc.context.rejectProposal.useMutation({
    onSuccess: () => {
      toast.success('Propozycja odrzucona');
      refresh();
    },
    onError,
  });

  const author = proposal.createdBy?.name ?? 'AI';
  const decided =
    proposal.status !== 'proposed' && proposal.decidedAt
      ? `${proposal.status === 'accepted' ? 'Przyjęta' : 'Odrzucona'} ${formatDateTimeShort(proposal.decidedAt)}${
          proposal.decidedBy ? ` · ${proposal.decidedBy.name}` : ''
        }${proposal.resultingVersion ? ` · wersja ${proposal.resultingVersion}` : ''}`
      : null;

  return (
    <li className="flex flex-col gap-3 border-b border-line py-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start">
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-x-2 text-body-s font-medium text-fg">
            <Sparkles size={14} strokeWidth={1.75} aria-hidden className="text-accent" />
            {proposal.file}
            <span className="font-normal text-fg-muted">· {MEMORY_SOURCE_TYPE_LABELS[proposal.source.type]}</span>
          </p>
          <p className="text-caption text-fg-muted">
            {author} · {formatDateTimeShort(proposal.createdAt)}
            {proposal.baseVersion ? ` · na podstawie wersji ${proposal.baseVersion}` : ''}
          </p>
        </div>
        <Status tone={MEMORY_PROPOSAL_STATUS_TONES[proposal.status]}>
          {MEMORY_PROPOSAL_STATUS_LABELS[proposal.status]}
        </Status>
      </div>
      {proposal.source.excerpt && (
        <blockquote className="border-l-2 border-line-strong pl-3 text-body-s text-fg-secondary">
          {proposal.source.excerpt}
        </blockquote>
      )}
      {proposal.stale && (
        <ContextBanner tone="warning">
          Profil zmienił się od czasu propozycji (bieżąca wersja {proposal.currentVersion}). Odrzuć ją albo poproś o
          nową.
        </ContextBanner>
      )}
      {proposal.status === 'proposed' && proposal.approvalId && (
        <p className="flex items-center gap-1.5 text-caption text-fg-muted">
          <ShieldCheck size={14} strokeWidth={1.75} aria-hidden />
          Czeka też w Akceptacjach. Decyzja tutaj zamknie akceptację.
        </p>
      )}
      <DiffView lines={proposal.diff} context={1} aria-label={`Zmiany w ${proposal.file}`} />
      {decided && <p className="text-caption text-fg-muted">{decided}</p>}
      {proposal.canDecide && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="ghost"
            size="s"
            className={TOUCH_BUTTON}
            icon={X}
            loading={reject.isPending}
            disabled={accept.isPending}
            onClick={() => reject.mutate({ id: proposal.id })}
          >
            Odrzuć
          </Button>
          <Button
            variant="secondary"
            size="s"
            className={TOUCH_BUTTON}
            icon={Check}
            loading={accept.isPending}
            disabled={proposal.stale || reject.isPending}
            onClick={() => accept.mutate({ id: proposal.id })}
          >
            Przyjmij
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Propozycje zmian pamięci (MVP §11: „zapamiętaj” → propozycja z diffem i źródłem → właściciel zatwierdza).
 * Przyjęcie = nowa wersja profilu; decydują właściciel i administrator.
 */
export function ProposalsSection({ pending }: { pending: number }) {
  const [filter, setFilter] = useState<Filter>('proposed');
  const list = trpc.context.listProposals.useQuery(filter === 'proposed' ? { status: 'proposed' } : {});

  let body: ReactNode;
  if (list.isPending) body = <ListSkeleton rows={2} />;
  else if (list.isError)
    body = (
      <ErrorState
        compact
        title="Nie udało się wczytać propozycji"
        onRetry={() => void list.refetch()}
        retrying={list.isFetching}
      />
    );
  else if (list.data.length === 0)
    body = (
      <EmptyState
        compact
        icon={Sparkles}
        title={filter === 'proposed' ? 'Brak propozycji do decyzji' : 'Brak propozycji'}
        description="Gdy AI zaproponuje zmianę pamięci (np. po poleceniu „zapamiętaj”), pojawi się tutaj z diffem i źródłem."
      />
    );
  else
    body = (
      <ul aria-label="Propozycje zmian pamięci">
        {list.data.map((p) => (
          <ProposalItem key={p.id} proposal={p} />
        ))}
      </ul>
    );

  return (
    <Section aria-label="Propozycje zmian pamięci">
      <SectionHeader
        tone="strong"
        title="Propozycje zmian pamięci"
        action={
          <Segmented
            aria-label="Propozycje"
            size="s"
            className="max-md:[&>button]:h-10"
            value={filter}
            onValueChange={setFilter}
            options={[
              {
                value: 'proposed',
                label:
                  pending > 0 ? (
                    <span className="inline-flex items-center gap-1.5">
                      Czekają <CountPill count={pending} tone="accent" />
                    </span>
                  ) : (
                    'Czekają'
                  ),
              },
              { value: 'all', label: 'Wszystkie' },
            ]}
          />
        }
      />
      {body}
    </Section>
  );
}
